import { Component, Input, OnChanges, OnInit, SimpleChanges, ViewChild } from '@angular/core'
import { select, Store } from '@ngrx/store'
import { EMPTY, from, of } from 'rxjs'
import { catchError, distinctUntilChanged, finalize, flatMap, mergeMap, switchMap, tap, toArray } from 'rxjs/operators'
import * as lodash from 'lodash'
import * as R from 'ramda'
import * as fromRoot from '@tv3/store/state'
import { isEmptyTable, localeCompareSort } from '@tokeet-frontend/utility'
import { TableType } from '@tv3/shared/empty-table/table-type'
import {
  AlertDialogService,
  Channel,
  ChannelNameTokens,
  ConfirmDialogService,
  Destroyable,
  InfoDialogService,
  RateMap,
  Rental,
  RentalService,
  selectAllChannels,
  Toaster,
  untilDestroy,
  updateRateMapLastSync,
  updateRentalComplete,
} from '@tokeet/tv3-platform'
import { PushRatesToApiConnectionPayload } from '@tv3/store/connection/connection.types'
import { ConnectionService } from '@tv3/store/connection/connection.service'
import { AddRateMappingDialogService } from '../add-rate-mapping-dialog/add-rate-mapping-dialog.service'
import { UserStorage } from '@tv3/services/utils/storage.service'
import { selectAllConnectionViews } from '@tv3/store/connection/connection.selectors'
import { ConnectionView } from '@tv3/store/connection/connection.view'
import { MatPaginator } from '@angular/material/paginator'
import { MatSort } from '@angular/material/sort'
import { MatTableDataSource } from '@angular/material/table'
import { MatSlideToggle } from '@angular/material/slide-toggle'

const HIDE_PUSH_RATES_TIP_KEY = 'HIDE_PUSH_RATES_TIP_KEY'

@Component({
  selector: 'app-rental-rate-mappings-table',
  templateUrl: './rental-rate-mappings-table.component.html',
  styleUrls: ['./rental-rate-mappings-table.component.scss'],
})
export class RentalRateMappingsTableComponent extends Destroyable implements OnInit, OnChanges {
  @Input() rental: Rental

  @ViewChild(MatPaginator, { static: true }) paginator: MatPaginator
  @ViewChild(MatSort, { static: true }) sort: MatSort

  channels: Record<string, Channel> = {}
  updatingSync: Record<string, boolean> = {}

  displayedColumns = ['channelName', 'propertyId', 'roomId', 'rateName', 'category', 'created', 'autoSync', 'edit']

  dataSource = new MatTableDataSource<RateMap>()
  tableType = TableType.RentalRateMappings
  isEmptyTable$ = isEmptyTable(this.dataSource)
  connections: ConnectionView[]

  constructor(
    private store: Store<fromRoot.State>,
    private toaster: Toaster,
    private storage: UserStorage,
    private infoDialog: InfoDialogService,
    private alertDialog: AlertDialogService,
    private confirmDialog: ConfirmDialogService,
    private rentalService: RentalService,
    private connectionService: ConnectionService,
    private addRateMappingDialog: AddRateMappingDialogService
  ) {
    super()
  }

  ngOnInit() {
    this.dataSource.paginator = this.paginator
    this.dataSource.sort = this.sort
    this.dataSource.sortData = localeCompareSort
    this.dataSource.data = this.getRateMappings()

    this.store
      .select(selectAllChannels)
      .pipe(
        distinctUntilChanged(),
        tap((channels) => {
          this.channels = lodash.keyBy(channels, (channel) => channel.id)
        }),
        untilDestroy(this)
      )
      .subscribe()

    this.store.pipe(select(selectAllConnectionViews), untilDestroy(this)).subscribe((connections) => {
      this.connections = connections
    })
  }

  rateMapHasConnection(map: RateMap) {
    const found = R.find((c: ConnectionView) => {
      const mapRoomId = R.when(R.is(Number), R.toString)(map.roomId)
      const connectionRoomId = R.when(R.is(Number), R.toString)(c.roomId)
      const mapPropertyId = R.when(R.is(Number), R.toString)(map.propertyId)
      const connectionPropertyId = R.when(R.is(Number), R.toString)(c.propertyId)

      return (
        connectionRoomId === mapRoomId &&
        connectionPropertyId === mapPropertyId &&
        map.rentalId === c.rentalId &&
        map.channelId === c.channelId
      )
    }, this.connections)

    return !R.isNil(found)
  }

  ngOnChanges(changes: SimpleChanges) {
    this.rental = lodash.get(changes, ['rental', 'currentValue'], this.rental)
    this.dataSource.data = this.getRateMappings()
  }

  getRateMappings() {
    return this.rental.rateMap || []
  }

  onChangeSync(rateMapping: RateMap, slideToggle: MatSlideToggle) {
    this.updatingSync[rateMapping.key] = true

    this.rentalService
      .setRateMappingAutoSync(this.rental, rateMapping, !rateMapping.autoSync)
      .pipe(
        tap((rateMappings) => {
          this.toaster.success('Rate mapping updated successfully.')
          this.updateRental(rateMappings)
        }),
        catchError((error) => {
          slideToggle.toggle()
          this.toaster.error('Unable to update rate mapping.')
          return of(error)
        }),
        finalize(() => {
          this.updatingSync[rateMapping.key] = false
        })
      )
      .subscribe()
  }

  onPushAllRates() {
    this.pushRates(this.dataSource.data)
  }

  onPushRate(rateMapping: RateMap) {
    this.pushRates([rateMapping])
  }

  onAdd() {
    const dialogRef = this.addRateMappingDialog.open({
      rental: this.rental,
      rateMappings: this.dataSource.data,
    })

    dialogRef.afterClosed().subscribe((result) => {
      if (result && result.rateMappings) {
        this.updateRental(result.rateMappings)
      }
    })
  }

  onDelete(rateMapping: RateMap) {
    this.confirmDialog.confirm().subscribe(() => {
      this.deleteRateMapping(rateMapping)
    })
  }

  private pushRates(rateMappings: RateMap[]) {
    const baseRate = this.rental.baseRate
    if (!baseRate || !baseRate.nightly) {
      this.alertDialog.open({
        title: 'Warning',
        body: `A Base Rate must be set in order to properly push rates to your connected channels. Please navigate to the Settings Tab
          and enter the required values before returning here to re-attempt your rate push.`,
      })
      return
    }

    of(rateMappings)
      .pipe(
        flatMap((rateMap) => from(rateMap)),
        mergeMap((rateMap) => {
          const payload = rateMap.serialize() as PushRatesToApiConnectionPayload
          payload.rental_id = this.rental.id
          return this.connectionService.pushRates(rateMap.api as ChannelNameTokens, rateMap.channelId, payload)
        }),
        toArray(),
        switchMap(() => {
          this.store.dispatch(updateRateMapLastSync({ rateMaps: R.reject(R.isNil)(rateMappings) }))

          const hideTip = this.storage.get(HIDE_PUSH_RATES_TIP_KEY, false)
          if (hideTip) {
            return of(true)
          }
          return this.infoDialog
            .open({
              title: 'Tips',
              body: `After pushing rates from Tokeet, it is recommended that you check the channel rate calendar(s) for accuracy. Errors
          returned by the channel do not appear immediately, so it is a good practice to check that your changes have reflected properly.`,
              extra: 'Do not show this message again.',
            })
            .pipe(tap(({ extra }) => this.storage.set(HIDE_PUSH_RATES_TIP_KEY, !!extra)))
        }),
        catchError((res) => {
          this.toaster.error('Unable to schedule rate push.', '', res)
          return EMPTY
        })
      )
      .subscribe(() => {
        this.toaster.info('Rate push scheduled.')
      })
  }

  private deleteRateMapping(rateMapping: RateMap) {
    this.rentalService
      .deleteRateMapping(this.rental.id, rateMapping.key)
      .pipe(
        tap((rateMappings) => {
          this.toaster.success('Rate mapping deleted successfully.')
          this.updateRental(rateMappings)
        }),
        catchError((error) => {
          this.toaster.error('Unable to delete rate mapping.')
          return of(error)
        })
      )
      .subscribe()
  }

  private updateRental(rateMap: RateMap[]) {
    this.store.dispatch(
      updateRentalComplete({
        update: {
          id: this.rental.id,
          changes: { rateMap },
        },
      })
    )
  }
}
